/* eslint-disable jira/restricted/fedramp-isolated-cloud */
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
// eslint-disable-next-line jira/import-whitelist
import { isFedRamp } from '@atlaskit/atlassian-context/is-fedramp';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import type { RouteResource } from '@atlassian/react-resource-router';
import {
	forgeProjectModuleResource,
	forgeServiceDeskQueueModuleResource,
} from '@jira/navigation-apps__resources/src/controllers/forge/index.tsx';
import { jsmProjectMenuContentSidebarResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/jsm-customize-sidebar/index.tsx';
import { staticJSMProjectConnectAddonsNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-jsm-connect-addons-queues/index.tsx';
import { staticSidebarJsmReportsResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-jsm-reports-sidebar/index.tsx';
import { staticProjectSidebarConnectAddonsNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar-connect-addons/index.tsx';
import { staticProjectReportsSidebarConnectAddonsNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar-reports-connect-addons/index.tsx';
import { staticProjectSidebarNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import { staticSidebarShortcutsResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-sidebar-shortcuts/index.tsx';
import { getBoardDetailsResource } from '@jira/router-resources__classic-projects/src/getBoardDetailsResource.tsx';
import { getEditModelResource } from '@jira/router-resources__classic-projects/src/getEditModelResource.tsx';
import { getRapidboardResources } from '@jira/router-resources__classic-projects/src/getRapidboardResources.tsx';
import { kbConfluenceContextResource } from '@jira/router-resources__confluence-context/src/kbConfluenceContextResource.tsx';
import { getConsolidationStateResource } from '@jira/router-resources__eoc-tenant-info/src/getConsolidationStateResource.tsx';
import { issueCollapsibleGroupDataResource } from '@jira/router-resources__issue-view/src/issueCollapsibleGroupDataResource.tsx';
import { jsmFeaturesEnabledResource } from '@jira/router-resources__jsm-features-enabled/src/jsmFeaturesEnabledResource.tsx';
import {
	jsmGracePeriodAnnouncementGpEndDateResource,
	jsmGracePeriodAnnouncementDisplayStateResource,
} from '@jira/router-resources__jsm-grace-period-announcements/src/useJsmGracePeriodAnnouncementGpEndDateResource.tsx';
import {
	getNavigationResources,
	getNavigationSidebarProjectSettingsResource,
} from '@jira/router-resources__navigation/src/getNavigationResources.tsx';
import { postOfficeAssetsMenuItemFreeUsersPlacementResource } from '@jira/router-resources__post-office/src/conditionalPostOfficeJiraTopBannerResource.tsx';
import {
	projectContextResource,
	projectContextResourceWithCache,
} from '@jira/router-resources__project-context/src/projectContextResource.tsx';
import { serviceDeskAnalyticsContextResource } from '@jira/router-resources__service-desk-analytics-context/src/useServiceDeskAnalyticsContext.tsx';
import { getCalendarFilterFieldsResource } from '@jira/router-resources__service-desk-calendar/src/useCalendarFilterResource.tsx';
import { serviceDeskContextResource } from '@jira/router-resources__service-desk-context/src/serviceDeskContextResource.tsx';
import { servicedeskIsItsmProjectResource } from '@jira/router-resources__service-desk-is-itsm-project/src/useIsItsmTemplateProject.tsx';
import { servicedeskPracticesResource } from '@jira/router-resources__service-desk-practices/src/useWorkCategoriesAllSupported.tsx';
import {
	requiredFrontendProperties,
	requiredProjectProperties,
	firstProjectResource,
	isSimplifiedNavResource,
} from '@jira/router-resources__service-desk-project-getting-started/src/firstProjectResource.tsx';
import { nav3QueueGroupsStateResource } from '@jira/router-resources__service-desk-queues/src/nav3QueueGroupsStateResource.tsx';
import { queuesAsTabsProjectPropertyResource } from '@jira/router-resources__service-desk-queues/src/queuesAsTabsProjectPropertyResource.tsx';
import {
	priorityGroupByProjectIdResourceSsr,
	queuesNavigationItems,
	queueNav4SidebarSelectionResource,
	queueSidebarQueuesCategoryStateResource,
	triageButtonPermissionResource,
} from '@jira/router-resources__service-desk-queues/src/queuesNavigationItems.tsx';
import { virtualAgentOnboardingResource } from '@jira/router-resources__service-desk-virtual-agent/src/virtualAgentOnboardingResource.tsx';
import { featuresResource } from '@jira/router-resources__service-project-features/src/featuresResource.tsx';
import { incidentExperimentTraitResource } from '@jira/router-resources__software-incidents/src/incidentExperimentTraitResource.tsx';
import { uifBoardResource } from '@jira/router-resources__uif-board/src/uifBoardResource.tsx';
import { resourceWithCondition } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
// eslint-disable-next-line jira/import-whitelist
import { workspaceResource } from '@jira/servicedesk__insight-common-services-workspace-context/src/workspaceResource.tsx';

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getServiceDeskNavigationResources = (): RouteResource<any>[] => [
	...getNavigationResources(),
	// these have been expanded from getNavigationSidebarProjectResource - please examine whether your route requires these resources in nav4 and remove them if needed
	// A guide to check & cleanup: https://hello.atlassian.net/wiki/x/Q8XybwE
	incidentExperimentTraitResource,
	projectContextResource,
	forgeProjectModuleResource,
	staticSidebarShortcutsResource,
	staticProjectSidebarNavigationResource,
	staticProjectSidebarConnectAddonsNavigationResource,
	// end of resources expanded from getNavigationSidebarProjectResource
	...getServiceDeskNavigationSingleResources(),
	resourceWithCondition(
		() => fg('jsm_gp_announcements_banner'),
		jsmGracePeriodAnnouncementDisplayStateResource,
	),
	resourceWithCondition(
		() => fg('jsm_gp_announcements_banner'),
		jsmGracePeriodAnnouncementGpEndDateResource,
	),
	postOfficeAssetsMenuItemFreeUsersPlacementResource,
	resourceWithCondition(
		() => fg('grw-214_jsm_jpd_simplified_nav_bar_killswitch'),
		isSimplifiedNavResource,
	),
	resourceWithCondition(
		() =>
			// eslint-disable-next-line jira/ff/no-preconditioning -- Main WFO gate must block status-control feature resource preloading when the parent feature is off.
			isFeatureEnabled('jira-service-management', 'jsm-workforce') &&
			fg('jsm_workforce_optimization_main') &&
			expVal('jsm_workforce_optimization_status_control', 'isEnabled', false),
		jsmFeaturesEnabledResource,
	),
];

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getServiceDeskNavigationSingleResources = (): RouteResource<any>[] => [
	// projectContextResource is a dependency to the priorityGroupsResource
	projectContextResource,
	queueNav4SidebarSelectionResource,
	queueSidebarQueuesCategoryStateResource,
	...getServiceDeskExclusiveNavigationSingleResources(),
	...getServiceDeskSharedNavigationSingleResources(),
];

/**
 * These resources only work correctly for Service Desk projects.
 * They will throw errors if used on non-Service Desk projects.
 */
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getServiceDeskExclusiveNavigationSingleResources = (): RouteResource<any>[] => [
	priorityGroupByProjectIdResourceSsr,
	featuresResource,
	staticSidebarJsmReportsResource,
	getConsolidationStateResource(),
	servicedeskIsItsmProjectResource,
	nav3QueueGroupsStateResource,
];

/**
 * These resources are able to handle both Service Desk and non-Service Desk projects.
 */
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getServiceDeskSharedNavigationSingleResources = (): RouteResource<any>[] => [
	queuesNavigationItems,
	resourceWithCondition(() => fg('jsm_queues_horizontal_nav'), queuesAsTabsProjectPropertyResource),
	requiredProjectProperties,
	requiredFrontendProperties,
	forgeServiceDeskQueueModuleResource,
	staticJSMProjectConnectAddonsNavigationResource,
	staticProjectReportsSidebarConnectAddonsNavigationResource,
	// for Getting Started menu item
	// resourceWithCondition(isNav4ResourcesFixEnabled, requiredFrontendProperties),
	projectContextResourceWithCache,
	firstProjectResource,
	jsmProjectMenuContentSidebarResource,
];

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getServiceDeskSettingsResources = (): RouteResource<any>[] => [
	...getNavigationResources(),
	// these have been expanded from getNavigationSidebarProjectResource - please examine whether your route requires these resources in nav4 and remove them if needed
	// A guide to check & cleanup: https://hello.atlassian.net/wiki/x/Q8XybwE
	incidentExperimentTraitResource,
	projectContextResource,
	forgeProjectModuleResource,
	staticSidebarShortcutsResource,
	staticProjectSidebarNavigationResource,
	staticProjectSidebarConnectAddonsNavigationResource,
	// end of resources expanded from getNavigationSidebarProjectResource
	...getNavigationSidebarProjectSettingsResource(),
	servicedeskPracticesResource,
	serviceDeskContextResource,
	serviceDeskAnalyticsContextResource,
	firstProjectResource,
	resourceWithCondition(
		() => fg('grw-214_jsm_jpd_simplified_nav_bar_killswitch'),
		isSimplifiedNavResource,
	),
	resourceWithCondition(
		() =>
			!fg('hide_vsa_endpoints') &&
			(fg('aem_migration_jira')
				? isFeatureEnabled('jira-service-management', 'jsm-help-center-virtual-agent')
				: !isFedRamp()),
		virtualAgentOnboardingResource,
	),
	resourceWithCondition(
		() =>
			// eslint-disable-next-line jira/ff/no-preconditioning -- Main WFO gate must block status-control feature resource preloading when the parent feature is off.
			(isFeatureEnabled('jira-service-management', 'jsm-workforce') &&
				fg('jsm_workforce_optimization_main') &&
				expVal('jsm_workforce_optimization_status_control', 'isEnabled', false)) ||
			fg('jsm_wfo_agent_presence'),
		jsmFeaturesEnabledResource,
	),
];

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getServicedeskPageContextResources = (): RouteResource<any>[] => [
	projectContextResourceWithCache,
	serviceDeskContextResource,
	serviceDeskAnalyticsContextResource,
	resourceWithCondition(
		() => fg('jsm_gp_announcements_banner'),
		jsmGracePeriodAnnouncementDisplayStateResource,
	),
	resourceWithCondition(
		() => fg('jsm_gp_announcements_banner'),
		jsmGracePeriodAnnouncementGpEndDateResource,
	),
	postOfficeAssetsMenuItemFreeUsersPlacementResource,
	resourceWithCondition(
		() =>
			// eslint-disable-next-line jira/ff/no-preconditioning -- Main WFO gate must block status-control feature resource preloading when the parent feature is off.
			(isFeatureEnabled('jira-service-management', 'jsm-workforce') &&
				fg('jsm_workforce_optimization_main') &&
				expVal('jsm_workforce_optimization_status_control', 'isEnabled', false)) ||
			fg('jsm_wfo_agent_presence'),
		jsmFeaturesEnabledResource,
	),
];

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getQueuesResources = (): RouteResource<any>[] => [
	...getServicedeskPageContextResources(),
	...getServiceDeskNavigationResources(),
	getCalendarFilterFieldsResource(),
	resourceWithCondition(() => fg('corcap-7272-wfm-in-product-nudge'), jsmFeaturesEnabledResource),
];

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getTriageButtonPermissionResource = (): RouteResource<any>[] => [
	triageButtonPermissionResource,
];

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getKnowledgeBaseResources = (): RouteResource<any>[] => [kbConfluenceContextResource];

export const getInsightResources = (): RouteResource<string | null>[] => [workspaceResource];

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getQueueConfigResource = (): RouteResource<any>[] => [projectContextResource];

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getQueueViewConfigResource = (): RouteResource<any>[] => [
	getEditModelResource(),
	getBoardDetailsResource(),
	projectContextResource,
	uifBoardResource,
	...getRapidboardResources(),
];

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const getIssueViewResources = (): RouteResource<any>[] => [
	resourceWithCondition(
		() => fg('jfp-magma-ssr-iv-collapse-group'),
		issueCollapsibleGroupDataResource,
	),
];
